<template>
  <teleport to="body">
    <div
      ref="toolbar"
      class="tiptap-toolbar fixed z-[9998] rounded-md border border-solid p-1 shadow-md space-y-2"
      :style="{
        top: position.top,
        left: position.left,
        bottom: position.bottom,
        right: position.right,
        transform: transformValue,
      }"
      :class="{
        hidden: !isFocused,
        'border-gray-100 bg-gray-10': inverse,
        'bg-gray-900 border-gray-800 text-white': !inverse,
      }"
      data-testid="tiptap-toolbar"
    >
      <ToolBarRow
        v-for="(row, rowIndex) in rows"
        :key="'toolbar-row-' + rowIndex"
        :editor="editor"
        :config="config"
        :properties-string="row"
        :inverse="inverse"
      />
    </div>
  </teleport>
</template>

<script setup lang="ts">
import ToolBarRow from "./ToolBarRow.vue";
import IEditorConfig from "../../types/IEditorConfig";
import { Editor } from "@tiptap/core";
import { computed, onMounted, ref, onBeforeUnmount, watch } from "vue";

const props = defineProps<{
  editor: Editor;
  isFocused: boolean;
  positions: {
    top: string;
    left: string;
    bottom: string;
    right: string;
  };
  heightOfElement: number;
  config: IEditorConfig;
  layoutOptions: string;
  inverse?: boolean;
  center?: boolean;
}>();

const toolbar = ref<HTMLElement | null>(null);
const transformValue = ref("translate(-50%, -100%)");
const position = ref(props.positions);

const isFocused = computed(() => props.isFocused);
const heightOfElement = computed(() => props.heightOfElement);

const rows = computed(() => {
  const rows = props.layoutOptions.split("|");
  return rows.filter((row) => row && row.trim() !== "");
});

function setTransfrom() {
  if (!toolbar.value || !props.isFocused) return;
  const toolbarBounding = toolbar.value.getBoundingClientRect();
  if (props.positions.top !== "auto") {
    let topValue = props.positions.top;
    let leftValue = props.positions.left;
    if (parseInt(props.positions.top) < toolbarBounding.height) {
      transformValue.value = "translate(-50%, 0%)";
      topValue = `${parseInt(props.positions.top) + props.heightOfElement + 10}px`;
    } else {
      transformValue.value = "translate(-50%, -100%)";
    }
    const realLeft = parseInt(props.positions.left) - toolbarBounding.width / 2;
    const realRight = realLeft + toolbarBounding.width;
    if (realLeft < 0) {
      leftValue = parseInt(props.positions.left) - realLeft + 2 + "px";
    }
    if (realRight > document.documentElement.clientWidth) {
      leftValue =
        parseInt(props.positions.left) -
        (realRight - document.documentElement.clientWidth + 2) +
        "px";
    }
    position.value = {
      top: topValue,
      left: leftValue,
      bottom: "auto",
      right: "auto",
    };
  } else {
    position.value = props.positions;
    if (props.center) {
      transformValue.value = "translate(-50%, 0%)";
    } else {
      transformValue.value = "translate(0%, 0%)";
    }
  }
}

watch(heightOfElement, () => {
  if (!isFocused.value) return;
  setTransfrom();
});

onMounted(() => {
  // The toolbar is mounted only while its editor is focused, so position it
  // right away - there is no unfocused instance waiting for isFocused to flip.
  setTimeout(() => {
    setTransfrom();
  }, 10);

  window.addEventListener("scroll", setTransfrom);
  window.addEventListener("resize", setTransfrom);
  const mainScrollArea = document.getElementById("main-scroll-area");
  if (mainScrollArea) {
    mainScrollArea.addEventListener("scroll", setTransfrom);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", setTransfrom);
  window.removeEventListener("resize", setTransfrom);
  const mainScrollArea = document.getElementById("main-scroll-area");
  if (mainScrollArea) {
    mainScrollArea.removeEventListener("scroll", setTransfrom);
  }
});
</script>

<style>
.tiptap-toolbar * {
  box-sizing: border-box;
}
.tiptap-toolbar svg {
  display: block;
}
</style>
